import { httpServices } from '../../../apis'
import type { PaginatedQueryResponse, QueryFnParams } from '../../../types'

import type { ShiftRQSchema, ShiftSchema } from './types/Shift.schema'

/**
 * Fetch one shift
 *
 */
export type FetchShiftOptions = QueryFnParams<{
  pathParams: { id?: number }
}>

export const fetchShift = async (opts: FetchShiftOptions) => {
  const response = await httpServices.core.get<ShiftRQSchema>(
    `/shifts/${opts?.pathParams.id}`
  )
  return response.data
}

/**
 * Fetch multiple shifts
 *
 */
export type FetchShiftsOptions = QueryFnParams<{
  queryParams: {
    membership_id: number
    user_contract_id: number
    location_id: number
    team_id: number
    start_date: 'YYYY-MM-DD' | string
    end_date: 'YYYY-MM-DD' | string
    'membership_ids[]': number[]
    include_without_end_date: boolean
  }
}>

export const fetchShifts = async (opts: FetchShiftsOptions) => {
  const response = await httpServices.core.get<ShiftSchema>(
    `/accounts/${httpServices.authInfo.accountId}/shifts`,
    {
      params: opts?.queryParams
    }
  )
  return response.data
}

export type CreateShiftOptions = QueryFnParams<{
  payload: Partial<ShiftSchema | ShiftRQSchema>
}>

export const createShift = async (opts: CreateShiftOptions) => {
  const response = await httpServices.core.post(`/shifts`, opts.payload)
  return response.data
}

/**
 * Fetch unassigned shifts specifically
 */
export type FetchUnassignedShiftsData = {
  shifts: ShiftSchema[]
}

export type FetchUnassignedShiftsOptions = QueryFnParams<{
  queryParams: {
    location_id?: number
    team_id?: number
    start_date: 'YYYY-MM-DD' | string
    end_date: 'YYYY-MM-DD' | string
  }
}>

export const fetchUnassignedShifts = async (
  opts: FetchUnassignedShiftsOptions,
  pageParam?: number | undefined
) => {
  const response = await httpServices.core.get<
    PaginatedQueryResponse<FetchUnassignedShiftsData, 'pages'>
  >(`/accounts/${httpServices.authInfo.accountId}/shifts`, {
    params: {
      ...opts?.queryParams,
      unassigned: true,
      page: pageParam,
      with_permissions: true
    }
  })
  return response.data
}

export type UpdateShiftOptions = QueryFnParams<{
  pathParams: {
    shiftId: number
  }
  payload: Partial<ShiftSchema | ShiftRQSchema> & {
    rest?: {
      note?: string
      type?: string
    }
  }
}>

export const updateShift = async (opts: UpdateShiftOptions) => {
  const response = await httpServices.core.put(
    `/shifts/${opts?.pathParams.shiftId}`,
    opts.payload
  )
  return response.data
}

export type DeleteShiftOptions = QueryFnParams<{
  pathParams: {
    shiftId: number
  }
  queryParams: {
    notify_user: boolean
  }
}>

export const deleteShift = async (opts: DeleteShiftOptions) => {
  const response = await httpServices.core.delete(
    `/shifts/${opts?.pathParams.shiftId}`,
    {
      params: opts?.queryParams
    }
  )
  return response.data
}
